/**
 * Push Notification Prompt Component
 *
 * A soft prompt shown once the user is logged in, explaining the benefits before triggering
 * the OS permission dialog.
 *
 * NATIVE ONLY (iOS / Android). Push on the web was Web Push, which is delivered by a service
 * worker — and Card 30 retired the PWA and its worker, so the web has no delivery path left.
 * Prompting a browser user for a permission we could not act on would be a lie, so this
 * renders nothing there. Native push is FCM/APNs and is unaffected.
 *
 * Features:
 * - Custom UI explaining notification benefits
 * - Dismissable with localStorage persistence
 * - Triggers permission dialog when user opts in
 */

import { useState, useEffect } from 'react';
import { X, Bell, MessageSquare, TrendingUp, Shield } from 'lucide-react';
import { isNative } from '@/utils/platform';
import { useAuth } from '@/contexts/AuthContext';
import {
  requestPushPermission,
  registerForPushNotifications,
  checkPushPermissionStatus,
  type PushPermissionStatus
} from '@/services/capacitor/pushNotifications';
import { useToast } from '@/contexts/ToastContext';

const STORAGE_KEY = 'push-notification-prompt-dismissed';
const DAYS_BEFORE_REPROMPT = 14; // Show again after 2 weeks if dismissed

interface PushNotificationPromptProps {
  /** Delay in ms before showing the prompt (default: 2000) */
  delay?: number;
  /** Callback when user enables notifications */
  onEnabled?: () => void;
  /** Callback when user dismisses the prompt */
  onDismissed?: () => void;
}

export function PushNotificationPrompt({
  delay = 2000,
  onEnabled,
  onDismissed
}: PushNotificationPromptProps) {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const { isAuthenticated } = useAuth();
  const { showWarning } = useToast();

  useEffect(() => {
    console.log('[PushPrompt] Checking conditions:', { isNative, isAuthenticated });
    if (!isAuthenticated) {
      console.log('[PushPrompt] Skipping - not authenticated');
      return;
    }

    // Native only. There is no web push delivery path any more (see the note above).
    if (!isNative) {
      console.log('[PushPrompt] Skipping - push is native-only');
      return;
    }

    const checkAndShowPrompt = async () => {
      // Check if already dismissed recently
      const dismissed = localStorage.getItem(STORAGE_KEY);
      console.log('[PushPrompt] Dismissed state:', dismissed);
      if (dismissed) {
        const dismissedTime = parseInt(dismissed, 10);
        const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
        console.log('[PushPrompt] Days since dismissed:', daysSinceDismissed);
        if (daysSinceDismissed < DAYS_BEFORE_REPROMPT) {
          console.log('[PushPrompt] Still in cooldown period, not showing');
          return;
        }
      }

      // Check current permission status
      const status = await checkPushPermissionStatus();
      console.log('[PushPrompt] Permission status:', status);

      // Only show prompt if permission hasn't been decided yet
      if (status === 'prompt') {
        // Show after delay to let user settle into the app
        console.log('[PushPrompt] Will show prompt after', delay, 'ms delay');
        setTimeout(() => {
          console.log('[PushPrompt] Showing prompt now');
          setShowPrompt(true);
        }, delay);
      } else {
        console.log('[PushPrompt] Permission already decided:', status);
      }
    };

    checkAndShowPrompt();
  }, [delay, isAuthenticated]);

  const handleEnable = async () => {
    setIsRequesting(true);

    try {
      console.log('[PushPrompt] Requesting push permission...');
      let status: PushPermissionStatus = 'denied';

      try {
        status = await requestPushPermission();
        console.log('[PushPrompt] Permission result:', status);
      } catch (permError) {
        console.error('[PushPrompt] Permission request error:', permError);
        // Continue anyway - some devices may still allow registration
      }

      if (status === 'granted') {
        // Register for push notifications with full error protection
        try {
          console.log('[PushPrompt] Registering for push notifications...');

          // registerForPushNotifications owns the listeners and is idempotent, so this cannot
          // double-register — the handlers below are only a subscription (see the ownership
          // note in that module).
          await registerForPushNotifications({
            onRegistration: (_token) => {
              console.log('[PushPrompt] Successfully registered with token');
            },
            onRegistrationError: (error) => {
              // Log but don't crash - this is expected without Firebase credentials
              console.warn('[PushPrompt] Registration error (likely missing Firebase config):', error);
            }
          });

          console.log('[PushPrompt] Registration call completed');
        } catch (regError) {
          // Catch any registration errors but don't crash
          console.warn('[PushPrompt] Registration failed:', regError);
        }

        onEnabled?.();
      } else {
        // Denied. On Android 13+ (API 33) this is the POST_NOTIFICATIONS runtime dialog,
        // and the OS gives no further feedback — the user simply never receives anything
        // and has no idea why. Say so, and point at the only place it can be undone
        // (the system dialog will not be offered again).
        console.log('[PushPrompt] Permission denied, dismissing');
        showWarning(
          'Notifications are off',
          'Turn them on any time in your device Settings → Apps → Trade Loop → Notifications.'
        );
        handleDismiss();
      }

      setShowPrompt(false);
    } catch (error) {
      console.error('[PushPrompt] Error enabling notifications:', error);
      // Still close the prompt even on error
      setShowPrompt(false);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    setShowPrompt(false);
    onDismissed?.();
  };

  // Don't render if not showing
  if (!showPrompt) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleDismiss}
      />

      {/* Modal - compact height for mobile */}
      <div className="relative w-full max-w-sm animate-slide-up max-h-[70vh] overflow-hidden">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-y-auto max-h-[70vh]">
          {/* Compact header with gradient and icon */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 text-white text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 rounded-full mb-2">
              <Bell className="w-6 h-6" />
            </div>
            <h2 className="text-lg font-bold">Stay in the Loop</h2>
            {/* Deliberately does not name the hardware. This read "on your iPhone" on iPad,
                because Capacitor's getPlatform() returns "ios" for both — caught on an iPad Pro
                13" simulator during the Card 26 walkthrough, on the first authenticated screen a
                reviewer sees. "this device" is correct on iPhone, iPad, Android and the browser
                alike, so no platform branch is needed. */}
            <p className="text-blue-100 text-sm">
              Enable notifications on this device
            </p>
          </div>

          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Compact benefits list */}
          <div className="p-4 space-y-3">
            <div className="space-y-2">
              <div className="flex items-center gap-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
                  <MessageSquare className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">New message alerts</p>
              </div>

              <div className="flex items-center gap-3 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />
                </div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Referral updates</p>
              </div>

              <div className="flex items-center gap-3 p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <div className="flex-shrink-0 w-8 h-8 bg-purple-100 dark:bg-purple-800 rounded-full flex items-center justify-center">
                  <Shield className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                </div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Security alerts</p>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col gap-2 pt-1">
              <button
                onClick={handleEnable}
                disabled={isRequesting}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-xl transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg"
              >
                {isRequesting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Enabling...
                  </span>
                ) : (
                  'Enable Notifications'
                )}
              </button>

              <button
                onClick={handleDismiss}
                disabled={isRequesting}
                className="w-full py-2.5 px-6 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors font-medium text-sm"
              >
                Maybe Later
              </button>
            </div>

            {/* Privacy note */}
            <p className="text-xs text-center text-gray-500 dark:text-gray-500">
              Change anytime in Settings
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// CSS for slide-up animation
const styles = `
  @keyframes slide-up {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  .animate-slide-up {
    animation: slide-up 0.3s ease-out;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const existingStyle = document.getElementById('push-prompt-styles');
  if (!existingStyle) {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'push-prompt-styles';
    styleSheet.textContent = styles;
    document.head.appendChild(styleSheet);
  }
}
